import clsx from 'clsx';
import Container from '../../../layout/Container';
import { getContentImageProps, resolveImageAltText } from '@/lib/utils';
import LoopCapVideo from './LoopCapVideo';
import { RightArrowSvg } from '../Icons';
import ExternalLink from '@/components/elements/ExternalLink';

/**
 * WayfinderFullFeature component renders a two part section with title,
 * description, image OR video, and an optional cta link.
 */
export default function WayfinderFullFeature({
  backgroundColor,
  block,
  position
}) {
  const {
    light_text,
    title,
    description_rte,
    footnote,
    link_text,
    link,
    desktop_image,
    top_padding_off,
    image_right,
    vimeo_url,
    video_upload_date,
    video_title,
    mobile_image,
    _metadata
  } = block.full_feature;
  const { uid } = _metadata;

  return (
    <div
      id={`${uid}_${position}`}
      data-name={'wayfinder-full-feature'}
      className={clsx(
        `w-full bg-[${backgroundColor}] overflow-hidden`,
        top_padding_off ? 'pb-20 lg:pb-[120px]' : 'py-20 lg:py-[120px]'
      )}
    >
      <Container>
        <div
          className={`relative w-full grid-flow-col sm:grid sm:gap-10 lg:gap-20`}
        >
          <CTABlock
            light_text={light_text}
            title={title}
            descriptionRTE={description_rte}
            linkText={link_text}
            link={link?.[0]}
            footnote={footnote}
            className={clsx(
              'self-center text-center sm:text-left lg:px-10',
              light_text ? 'text-white' : 'text-black',
              image_right ? 'sm:order-1' : 'sm:order-2'
            )}
          />

          {vimeo_url && (
            <LoopCapVideo
              imageRight={image_right}
              vimeoURL={vimeo_url}
              position={position}
              videoUploadDate={video_upload_date}
              desktopImage={desktop_image}
              videoTitle={video_title}
              className={clsx(
                'relative order-1 aspect-square w-full self-center sm:max-h-[259px] sm:min-h-[259px] sm:max-w-[259px] sm:min-w-[259px] lg:max-h-[480px] lg:min-h-[480px] lg:max-w-[471px] lg:min-w-[471px]'
              )}
            />
          )}

          <ImageBlock
            desktopImage={desktop_image}
            mobileImage={mobile_image}
            imageRight={image_right}
            className={clsx(
              `order-1 mt-15 block h-auto w-full shrink-0 self-center sm:mt-0 sm:h-[259px] sm:w-[259px] lg:h-[480px] lg:w-[471px]`,
              { hidden: vimeo_url }
            )}
          />
        </div>
      </Container>
    </div>
  );
}

/**
 * ImageBlock component renders responsive images for desktop and mobile.
 */
const ImageBlock = ({ className, desktopImage, mobileImage }) => {
  const { srcSet: desktopSrcSet } = getContentImageProps({
    image: desktopImage
  });
  const { srcSet: mobileSrcSet, remainingProps } = getContentImageProps({
    image: mobileImage
  });

  return (
    <picture className={className}>
      <source media="(min-width: 1024px)" srcSet={desktopSrcSet} />
      <source srcSet={mobileSrcSet} />
      <img
        {...remainingProps}
        className="size-full object-cover"
        alt={resolveImageAltText(desktopImage)}
      />
    </picture>
  );
};

/**
 * CTABlock component renders a block with title, description, link, and footnote.
 */
const CTABlock = async ({
  light_text,
  title,
  descriptionRTE,
  linkText,
  link,
  footnote,
  className
}) => {
  return (
    <div className={className}>
      {title && <h2 className="h4 lg:h2 mb-10">{title}</h2>}
      <div
        dangerouslySetInnerHTML={{ __html: descriptionRTE }}
        className="body-r sm:body-l rte-simple mb-10"
      ></div>
      {link && linkText && (
        <div className="mb-10">
          <div className={`flex justify-center sm:justify-start`}>
            <RightArrowSvg
              className={`${light_text ? 'text-white' : 'text-black'}`}
            />
            <ExternalLink
              linkText={linkText}
              internalLink={link.url}
              linkStyling={`${light_text ? 'txt-btn-secondary-white' : 'txt-btn-secondary'} h6 lg:h5 ml-[10px] pb-1`}
            />
          </div>
        </div>
      )}
      {footnote && (
        <div
          className={`body-s ${light_text ? 'text-dawn-grey-30' : 'text-grey-text'} `}
        >
          {footnote}
        </div>
      )}
    </div>
  );
};
